import { InspectionJobDetail } from "../../../../../domains/jobs/models/InspectionJobDetail";
import { CONSTANTS } from "../../../../../constants";

/** Mirrors the lot summary's score card statuses so both screens rank a lot's steps identically. */
export enum ScheduleJobLifecycle {
  Active = "active",
  Pending = "pending",
  Complete = "complete"
}

export interface ScheduleJobDetail extends InspectionJobDetail {
  hasStarted: boolean;
  getStepLabel(): string;
  getEffectiveFilterDate(): Date;
  getLifecycle(isWithinReadyWindow: boolean): ScheduleJobLifecycle;
  isFeatureEnabled(): boolean;
}

export interface CreateScheduleJobDetailOptions {
  hasStarted?: boolean;
  bq15Enabled?: boolean;
  bq234Enabled?: boolean;
  bq6Enabled?: boolean;
  bq9Enabled?: boolean;
  isBq8Complete?: boolean;
  isChecklistMaterialized?: boolean;
}

export interface ScheduleCardDefinitionContext {
  bq15Enabled: boolean;
  bq234Enabled: boolean;
  bq6Enabled: boolean;
  bq9Enabled: boolean;
  isBq8Complete: boolean;
  isChecklistMaterialized: boolean;
}

export interface ScheduleCardDefinition {
  stepLabel: string;
  /** False for steps the CM never performs themselves (BQ7), which therefore never become active. */
  canBePerformedByCm: boolean;
  canBeStarted: (context: ScheduleCardDefinitionContext) => boolean;
  getEffectiveFilterDate: (job: ScheduleJobDetail) => Date;
  canUseIssueHistoryForStartedState: boolean;
  isFeatureEnabled: (context: ScheduleCardDefinitionContext) => boolean;
}

/**
 * A completed step moves to the day it was finished, unless it was finished ahead of schedule, in
 * which case it stays in the section its scheduled date already put it in.
 */
function getDefaultEffectiveFilterDate(job: ScheduleJobDetail): Date {
  if (job.isComplete && job.submittedAt) {
    return job.submittedAt > job.scheduledDate ? job.submittedAt : job.scheduledDate;
  }

  return job.scheduledDate;
}

function getScheduleCardDefinitionKey(buildQualityStep: number): string {
  return `BQ${buildQualityStep}`;
}

// BQ2, BQ3 and BQ4 are single-area inspections that share one feature flag and one card layout.
function createSingleAreaInspectionDefinition(
  buildQualityStep: number,
  isFeatureEnabled: (context: ScheduleCardDefinitionContext) => boolean
): ScheduleCardDefinition {
  return {
    stepLabel: getScheduleCardDefinitionKey(buildQualityStep),
    canBePerformedByCm: true,
    canBeStarted: () => true,
    getEffectiveFilterDate: getDefaultEffectiveFilterDate,
    canUseIssueHistoryForStartedState: true,
    isFeatureEnabled
  };
}

/** Doubles as the allow-list for the CM schedule: BQ7 is absent because the CCM owns it. */
export const scheduleCardDefinitions: Readonly<Record<string, ScheduleCardDefinition>> = {
  [`BQ${CONSTANTS.BUILD_QUALITY_STEPS.BQ1}`]: createSingleAreaInspectionDefinition(
    CONSTANTS.BUILD_QUALITY_STEPS.BQ1,
    context => context.bq15Enabled
  ),

  [`BQ${CONSTANTS.BUILD_QUALITY_STEPS.BQ2}`]: createSingleAreaInspectionDefinition(
    CONSTANTS.BUILD_QUALITY_STEPS.BQ2,
    context => context.bq234Enabled
  ),

  [`BQ${CONSTANTS.BUILD_QUALITY_STEPS.BQ3}`]: createSingleAreaInspectionDefinition(
    CONSTANTS.BUILD_QUALITY_STEPS.BQ3,
    context => context.bq234Enabled
  ),

  [`BQ${CONSTANTS.BUILD_QUALITY_STEPS.BQ4}`]: createSingleAreaInspectionDefinition(
    CONSTANTS.BUILD_QUALITY_STEPS.BQ4,
    context => context.bq234Enabled
  ),

  [`BQ${CONSTANTS.BUILD_QUALITY_STEPS.BQ5}`]: createSingleAreaInspectionDefinition(
    CONSTANTS.BUILD_QUALITY_STEPS.BQ5,
    context => context.bq15Enabled
  ),

  [`BQ${CONSTANTS.BUILD_QUALITY_STEPS.BQ6}`]: {
    stepLabel: "BQ6",
    canBePerformedByCm: true,
    canBeStarted: () => true,
    getEffectiveFilterDate: getDefaultEffectiveFilterDate,
    canUseIssueHistoryForStartedState: true,
    isFeatureEnabled: context => context.bq6Enabled
  },

  [`BQ${CONSTANTS.BUILD_QUALITY_STEPS.BQ8}`]: {
    stepLabel: "BQ8",
    canBePerformedByCm: true,
    canBeStarted: context => context.isChecklistMaterialized,
    getEffectiveFilterDate: getDefaultEffectiveFilterDate,
    canUseIssueHistoryForStartedState: true,
    isFeatureEnabled: () => true
  },

  [`BQ${CONSTANTS.BUILD_QUALITY_STEPS.BQ9}`]: {
    stepLabel: "BQ9",
    canBePerformedByCm: true,
    canBeStarted: context => context.isBq8Complete,
    getEffectiveFilterDate: getDefaultEffectiveFilterDate,
    canUseIssueHistoryForStartedState: false,
    isFeatureEnabled: context => context.bq9Enabled
  }
};

function getScheduleCardDefinition(buildQualityStep: number): ScheduleCardDefinition | undefined {
  return scheduleCardDefinitions[getScheduleCardDefinitionKey(buildQualityStep)];
}

export function canUseIssueHistoryForStartedState(job: { buildQualityStep: number }): boolean {
  return getScheduleCardDefinition(job.buildQualityStep)?.canUseIssueHistoryForStartedState ?? true;
}

export function createScheduleJobDetail(
  job: InspectionJobDetail,
  options: CreateScheduleJobDetailOptions = {}
): ScheduleJobDetail {
  const context: ScheduleCardDefinitionContext = {
    bq15Enabled: options.bq15Enabled ?? true,
    bq234Enabled: options.bq234Enabled ?? true,
    bq6Enabled: options.bq6Enabled ?? true,
    bq9Enabled: options.bq9Enabled ?? true,
    isBq8Complete: options.isBq8Complete ?? false,
    isChecklistMaterialized: options.isChecklistMaterialized ?? true
  };

  return {
    ...job,
    hasStarted: options.hasStarted ?? !!job.startedAt,

    getStepLabel(): string {
      return getScheduleCardDefinition(this.buildQualityStep)?.stepLabel
        ?? getScheduleCardDefinitionKey(this.buildQualityStep);
    },

    getEffectiveFilterDate(): Date {
      const definition = getScheduleCardDefinition(this.buildQualityStep);

      return definition?.getEffectiveFilterDate(this) ?? this.scheduledDate;
    },

    getLifecycle(isWithinReadyWindow: boolean): ScheduleJobLifecycle {
      if (this.isComplete) {
        return ScheduleJobLifecycle.Complete;
      }

      const definition = getScheduleCardDefinition(this.buildQualityStep);

      if (!definition || !definition.canBePerformedByCm || !definition.canBeStarted(context)) {
        return ScheduleJobLifecycle.Pending;
      }

      if (this.hasStarted) {
        return ScheduleJobLifecycle.Active;
      }

      return isWithinReadyWindow ? ScheduleJobLifecycle.Active : ScheduleJobLifecycle.Pending;
    },

    isFeatureEnabled(): boolean {
      const definition = getScheduleCardDefinition(this.buildQualityStep);

      return definition?.isFeatureEnabled(context) ?? false;
    }
  } as ScheduleJobDetail;
}
